import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import "./App.css";

const Button = ({ children, ...props }) => (
  <button className="btn" {...props}>{children}</button>
);

const FadeIn = ({ children }) => (
  <motion.section
    className="section"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
  >
    {children}
  </motion.section>
);

const Counter = ({ end, label }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const startTime = performance.now();

    const animate = (time) => {
      const progress = Math.min((time - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [end]);

  return (
    <div className="counter">
      <h2>{count}+</h2>
      <p>{label}</p>
    </div>
  );
};

export default function App() {
  const [language, setLanguage] = useState("en");
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", product: "", qty: "" });

  const content = {
    en: {
      heroTitle: "Aadhar Dairy Farm & Silage",
      heroSubtitle: "Better Feed \u2022 Better Health \u2022 Better Milk",

      aboutTitle: "About Us",
      aboutText:
        "Aadhar Dairy Farm & Silage is dedicated to empowering dairy farmers with premium-quality bale silage and reliable fodder solutions. Our mission is to improve livestock nutrition, enhance milk productivity, and reduce feeding costs through scientifically prepared silage and expert farm guidance.",

      silageTitle: "Silage Benefits",
      silagePoints: [
        "High nutritional value for better animal health",
        "Improves digestion and feed efficiency",
        "Boosts milk yield and fat content",
        "Reduces seasonal fodder shortages",
        "Minimizes feed wastage",
        "Ensures consistent quality year-round",
      ],

      servicesTitle: "Our Services",
      services: [
        "Premium Bale Silage Supply",
        "Green Fodder Solutions",
        "Dairy Farm Consultancy",
        "Livestock Nutrition Guidance",
        "Bulk Orders & Farm Delivery",
        "Custom Silage Planning",
      ],

      whyTitle: "Why Choose Us",
      whyPoints: [
        "Consistent premium silage quality",
        "Scientifically managed fermentation process",
        "Trusted by hundreds of farmers",
        "Affordable & cost-effective solutions",
        "Reliable logistics & timely delivery",
        "Dedicated farmer support",
      ],

      ownersTitle: "Our Leadership",

      impactTitle: "Our Impact",
      counters: [
        { end: 400, label: "Farmers Supported" },
        { end: 20, label: "Average Milk Yield Increase (%)" },
        { end: 6000, label: "Employment Generated" },
        { end: 300, label: "Tons Monthly Production" },
      ],

      galleryTitle: "Gallery",

      contactTitle: "Contact & Orders",
      contactText:
        "Have questions or want to place an order? Fill out the form below or contact us directly. Our team will assist you with product details, pricing, and delivery options.",

      submit: "Order via WhatsApp",
    },

    kn: {
      heroTitle: "ಆಧಾರ್ ಡೈರಿ ಫಾರ್ಮ್ & ಸೈಲೇಜ್",
      heroSubtitle: "ಉತ್ತಮ ಮೇವು \u2022 ಉತ್ತಮ ಆರೋಗ್ಯ \u2022 ಹೆಚ್ಚು ಹಾಲು",

      aboutTitle: "ನಮ್ಮ ಬಗ್ಗೆ",
      aboutText:
        "ಆಧಾರ್ ಡೈರಿ ಫಾರ್ಮ್ & ಸೈಲೇಜ್ ಉತ್ತಮ ಗುಣಮಟ್ಟದ ಬೇಲ್ ಸೈಲೇಜ್ ಮತ್ತು ಮೇವು ಪರಿಹಾರಗಳನ್ನು ಒದಗಿಸುವಲ್ಲಿ ಸಮರ್ಪಿತವಾಗಿದೆ. ನಮ್ಮ ಗುರಿ ಪಶುಗಳ ಆರೋಗ್ಯ ಸುಧಾರಣೆ, ಹಾಲು ಉತ್ಪಾದನೆ ಹೆಚ್ಚಳ ಮತ್ತು ರೈತರ ವೆಚ್ಚ ಕಡಿತವಾಗಿದೆ.",

      silageTitle: "ಸೈಲೇಜ್ ಪ್ರಯೋಜನಗಳು",
      silagePoints: [
        "ಉತ್ತಮ ಪೋಷಕಾಂಶ ಮತ್ತು ಪಶು ಆರೋಗ್ಯ",
        "ಜೀರ್ಣಕ್ರಿಯೆ ಸುಧಾರಣೆ",
        "ಹಾಲು ಉತ್ಪಾದನೆ ಮತ್ತು ಕೊಬ್ಬು ಹೆಚ್ಚಳ",
        "ಮೇವು ಕೊರತೆ ನಿವಾರಣೆ",
        "ಮೇವು ವ್ಯರ್ಥ ಕಡಿತ",
        "ವರ್ಷಪೂರ್ತಿ ಸಮಾನ ಗುಣಮಟ್ಟ",
      ],

      servicesTitle: "ನಮ್ಮ ಸೇವೆಗಳು",
      services: [
        "ಉತ್ತಮ ಗುಣಮಟ್ಟದ ಬೇಲ್ ಸೈಲೇಜ್",
        "ಹಸಿರು ಮೇವು ಪರಿಹಾರಗಳು",
        "ಡೈರಿ ಫಾರ್ಮ್ ಸಲಹೆ",
        "ಪಶು ಪೋಷಣಾ ಮಾರ್ಗದರ್ಶನ",
        "ಬಲ್ಕ್ ಆರ್ಡರ್ & ಡೆಲಿವರಿ",
        "ಕಸ್ಟಮ್ ಸೈಲೇಜ್ ಯೋಜನೆ",
      ],

      whyTitle: "ನಮ್ಮನ್ನು ಯಾಕೆ ಆಯ್ಕೆ ಮಾಡಬೇಕು",
      whyPoints: [
        "ಸ್ಥಿರ ಉತ್ತಮ ಗುಣಮಟ್ಟ",
        "ವೈಜ್ಞಾನಿಕ ಫರ್ಮೆಂಟೇಶನ್ ವಿಧಾನ",
        "ನೂರಾರು ರೈತರ ವಿಶ್ವಾಸ",
        "ಕಡಿಮೆ ವೆಚ್ಚದ ಪರಿಹಾರಗಳು",
        "ಸಮಯಕ್ಕೆ ಸರಿಯಾದ ಡೆಲಿವರಿ",
        "ರೈತರಿಗೆ ಸಮರ್ಪಿತ ಬೆಂಬಲ",
      ],

      ownersTitle: "ನಮ್ಮ ನಾಯಕತ್ವ",

      impactTitle: "ನಮ್ಮ ಪ್ರಭಾವ",
      counters: [
        { end: 400, label: "ರೈತರಿಗೆ ಬೆಂಬಲ" },
        { end: 20, label: "ಸರಾಸರಿ ಹಾಲು ಉತ್ಪಾದನೆ ಹೆಚ್ಚಳ (%)" },
        { end: 6000, label: "ಉದ್ಯೋಗ ಸೃಷ್ಟಿ" },
        { end: 300, label: "ತಿಂಗಳ ಉತ್ಪಾದನೆ (ಟನ್)" },
      ],

      galleryTitle: "ಗ್ಯಾಲರಿ",

      contactTitle: "ಸಂಪರ್ಕ & ಆರ್ಡರ್",
      contactText:
        "ನಿಮಗೆ ಪ್ರಶ್ನೆಗಳಿದ್ದರೆ ಅಥವಾ ಆರ್ಡರ್ ಮಾಡಲು ಬಯಸಿದರೆ, ಕೆಳಗಿನ ಫಾರ್ಮ್ ಭರ್ತಿ ಮಾಡಿ ಅಥವಾ ನೇರವಾಗಿ ಸಂಪರ್ಕಿಸಿ.",

      submit: "ವಾಟ್ಸಾಪ್ ಮೂಲಕ ಆರ್ಡರ್ ಮಾಡಿ",
    },
  };

  const t = content[language];

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.name || !form.phone || !form.product || !form.qty) {
      alert(language === "kn" ? "ದಯವಿಟ್ಟು ಎಲ್ಲಾ ವಿವರಗಳನ್ನು ನಮೂದಿಸಿ" : "Please fill all details");
      return;
    }

    const message =
      language === "kn"
        ? `ಹೊಸ ಆರ್ಡರ್:\nಹೆಸರು: ${form.name}\nಫೋನ್: ${form.phone}\nಉತ್ಪನ್ನ: ${form.product}\nಪ್ರಮಾಣ: ${form.qty}`
        : `New Order:\n\nName: ${form.name}\nPhone: ${form.phone}\nProduct: ${form.product}\nQuantity: ${form.qty}`;

    window.open(`https://wa.me/919916178805?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <div className="page">

      {/* Navbar */}
      <header className="navbar glass">
        <div className="navLeft">
          <img src="/logo.png" className="logo" alt="logo" />
          <span className="brand">Aadhar Dairy Farm & Silage</span>
        </div>

        <div className="langSwitch">
          <button onClick={() => setLanguage("en")} className={language === "en" ? "active" : ""}>EN</button>
          <button onClick={() => setLanguage("kn")} className={language === "kn" ? "active" : ""}>ಕನ್ನಡ</button>
        </div>
      </header>

      {/* Hero Video */}
      <section className="hero videoHero">
        <video autoPlay muted loop playsInline className="heroVideo">
          <source src="/gallery3.mp4" type="video/mp4" />
        </video>

        <div className="heroOverlay">
          <div className="heroContent">
            <h1>{t.heroTitle}</h1>
            <p className="heroSubtitle">{t.heroSubtitle}</p>
          </div>
        </div>
      </section>

      {/* About */}
      <FadeIn>
        <h3>{t.aboutTitle}</h3>
        <p>{t.aboutText}</p>
      </FadeIn>

      {/* Silage */}
      <FadeIn>
        <h3>{t.silageTitle}</h3>
        <ul className="benefitsList">
          {t.silagePoints.map(p => <li key={p}>{p}</li>)}
        </ul>
      </FadeIn>
      <FadeIn>
        <h3>{t.servicesTitle}</h3>
        <div className="servicesGrid">
          {t.services.map(service => (
            <div key={service} className="serviceCard">
              {service}
            </div>
          ))}
        </div>
      </FadeIn>

      <FadeIn>
        <h3>{t.whyTitle}</h3>
        <ul className="whyList">
          {t.whyPoints.map(point => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </FadeIn>

      <FadeIn>
        <h3 className="sectionTitle">
          {language === "kn"
            ? "ಡೈರಿ ಕ್ಷೇತ್ರದ ನಾಯಕತ್ವದಿಂದ ಮೆಚ್ಚುಗೆ"
            : "Recognized by Dairy Leadership"}
        </h3>

        <div className="recognitionSection">

          <div className="recognitionImage">
            <img src="/srgowdru2.jpg" alt="Sri S R Gowdru Visit" />
          </div>

          <div className="recognitionContent">

            <p className="quote">
              {language === "kn"
                ? "“ಡೈರಿ ಕ್ಷೇತ್ರದಲ್ಲಿ ಗುಣಮಟ್ಟದ ಪಶು ಪೋಷಣೆ ಅತ್ಯಂತ ಮುಖ್ಯವಾಗಿದೆ. ಆಧಾರ್ ಡೈರಿ ಫಾರ್ಮ್ & ಸೈಲೇಜ್ ಸಂಸ್ಥೆ ವೈಜ್ಞಾನಿಕವಾಗಿ ತಯಾರಿಸಿದ ಬೇಲ್ ಸೈಲೇಜ್ ಮೂಲಕ ರೈತರ ಅಭಿವೃದ್ಧಿಗೆ ಮಹತ್ವದ ಕೊಡುಗೆ ನೀಡುತ್ತಿದೆ. ಇಂತಹ ಪ್ರಯತ್ನಗಳು ಡೈರಿ ಕ್ಷೇತ್ರದ ಸ್ಥಿರ ಅಭಿವೃದ್ಧಿಗೆ ಸಹಕಾರಿಯಾಗಿವೆ. ನಾನು ವೈಜ್ಞಾನಿಕವಾಗಿ ಸರಿಯಾಗಿ ಫರ್ಮೆಂಟ್ ಮಾಡಿದ ಉತ್ತಮ ಗುಣಮಟ್ಟದ ಸೈಲೇಜ್ ಮಾತ್ರ ಮಾರಾಟ ಮಾಡಲು ಬಲವಾಗಿ ಸಲಹೆ ನೀಡುತ್ತೇನೆ.”"
                : "“Quality cattle nutrition is fundamental to improving milk productivity and strengthening the dairy sector. I appreciate the efforts of Aadhar Dairy Farm & Silage in promoting scientifically prepared bale silage solutions for farmers. Such initiatives contribute positively to sustainable dairy development. I strongly recommend supplying only properly fermented, high-quality silage.”"}
            </p>

            <h4>
              — {language === "kn" ? "ಶ್ರೀ ಎಸ್ ಆರ್ ಗೌಡ್ರು" : "Sri S R Gowdru"}
            </h4>

            <span className="designation">
              {language === "kn" ? (
                <>
                  ನಿರ್ದೇಶಕರು, ತುಮಕೂರು ಜಿಲ್ಲಾ ಹಾಲು ಉತ್ಪಾದಕರ ಒಕ್ಕೂಟ (ತುಮುಲ್)
                  <br />
                  ಮಾಜಿ ಅಧ್ಯಕ್ಷರು, ಕರ್ನಾಟಕ ರೇಷ್ಮೆ ಉದ್ಯಮ ಸಂಸ್ಥೆ (KSIC)
                </>
              ) : (
                <>
                  Director, TUMUL (Tumkur District Milk Union)
                  <br />
                  Former Chairman, Karnataka Silk Industries Corporation (KSIC)
                </>
              )}
            </span>



          </div>
        </div>
      </FadeIn>

      {/* Owners */}
      <FadeIn>
        <h3 className="sectionTitle">{t.ownersTitle}</h3>

        <div className="ownersGrid">

          <div className="ownerCard glassCard" onClick={() => setSelectedOwner("owner1")}>
            <img src="/owner1.png" alt="Abhilash Gowda" />
            <h4>
              {language === "kn"
                ? "ಅಭಿಲಾಶ್ ಗೌಡ"
                : "Abhilash Gowda"}
            </h4>
            <p>
              {language === "kn"
                ? "ಸ್ಥಾಪಕರು"

                : "Founder"}
            </p>
          </div>

          <div className="ownerCard glassCard" onClick={() => setSelectedOwner("owner2")}>
            <img src="/owner2.png" alt="Mohan Gowda" />
            <h4>
              {language === "kn"
                ? "ಮೋಹನ್ ಗೌಡ"
                : "Mohan Gowda"}
            </h4>
            <p>
              {language === "kn"
                ? "ಸಹ ಸ್ಥಾಪಕರು "
                : "Co-Founder "}
            </p>
          </div>

        </div>
      </FadeIn>





      {/* Impact */}
      <section className="impact">
        <h3>{t.impactTitle}</h3>
        <div className="counterGrid">
          {t.counters.map(c => (
            <Counter key={c.label} end={c.end} label={c.label} />
          ))}
        </div>
      </section>

      {/* Gallery */}
      <FadeIn>
        <h3>{t.galleryTitle}</h3>
        <div className="gallery">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((video) => (
            <div key={video} className="galleryItem">
              <video
                src={`/gallery${video}.mp4`}
                autoPlay
                muted
                loop
                playsInline
                controls
                className="galleryVideo"
              />
            </div>
          ))}
        </div>
      </FadeIn>

      {/* Contact */}
      <FadeIn>
        <h3 className="contactTitle">{t.contactTitle}</h3>

        <p className="contactText">{t.contactText}</p>

        {/* NEW WRAPPER */}
        <div className="contactContainer">

          <form className="form modernForm" onSubmit={handleSubmit}>
            <input
              placeholder={language === "kn" ? "ಹೆಸರು" : "Name"}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              placeholder={language === "kn" ? "ಫೋನ್ ಸಂಖ್ಯೆ" : "Phone Number"}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <input
              placeholder={language === "kn" ? "ಉತ್ಪನ್ನ" : "Product"}
              onChange={(e) => setForm({ ...form, product: e.target.value })}
            />
            <input
              placeholder={language === "kn" ? "ಪ್ರಮಾಣ" : "Quantity"}
              onChange={(e) => setForm({ ...form, qty: e.target.value })}
            />

            <Button type="submit">{t.submit}</Button>
          </form>

          <div className="contactInfo polishedContactInfo">
            <div className="contactCard">
              <Phone size={18} />
              <div>
                <strong>{language === "kn" ? "ಫೋನ್" : "Phone"}</strong>
                <span>+91 9916178805 / 9916178806</span>
              </div>
            </div>

            <div className="contactCard">
              <Mail size={18} />
              <div>
                <strong>{language === "kn" ? "ಇಮೇಲ್" : "Email"}</strong>
                <span>aadhardairyfarmandsilage@gmail.com</span>
              </div>
            </div>

            <div className="contactCard">
              <MapPin size={18} />
              <div>
                <strong>{language === "kn" ? "ವಿಳಾಸ" : "Address"}</strong>
                <span>Nejanthi, Hulikunte Hobli, Sira</span>
              </div>
            </div>
          </div>

        </div>
      </FadeIn>


      {/* Modal */}
      {/* Modal */}
      {selectedOwner && (
        <div className="modalOverlay" onClick={() => setSelectedOwner(null)}>
          <div className="modalCard" onClick={(e) => e.stopPropagation()}>
            <img src={`/${selectedOwner}.png`} alt="owner" />

            <h3>
              {selectedOwner === "owner1"
                ? (language === "kn" ? "ಅಭಿಲಾಶ್ ಗೌಡ" : "Abhilash Gowda")
                : (language === "kn" ? "ಮೋಹನ್ ಗೌಡ ಬಿಎಂ" : "Mohan Gowda BM")}
            </h3>

            <p>
              {selectedOwner === "owner1"
                ? (language === "kn"
                  ? "ಅಭಿಲಾಶ್ ಗೌಡ ಅವರು ಆಧಾರ್ ಡೈರಿ ಫಾರ್ಮ್ & ಸೈಲೇಜ್ ಸಂಸ್ಥಾಪಕರು. ವೈಜ್ಞಾನಿಕವಾಗಿ ತಯಾರಿಸಲಾದ ಬೇಲ್ ಸೈಲೇಜ್ ಪರಿಹಾರಗಳನ್ನು ರೈತರಿಗೆ ಪರಿಚಯಿಸಿ, ಪಶುಗಳ ಪೋಷಣಾ ಸುಧಾರಣೆ ಮತ್ತು ಹಾಲು ಉತ್ಪಾದನೆ ಹೆಚ್ಚಳದತ್ತ ಅವರು ಕೆಲಸ ಮಾಡುತ್ತಿದ್ದಾರೆ."
                  : "Abhilash Gowda is the Founder & Director of Aadhar Dairy Farm & Silage. With a strong passion for modern dairy farming, he has been instrumental in introducing scientifically prepared bale silage solutions. His vision focuses on improving livestock nutrition, increasing milk productivity, and supporting sustainable dairy practices.")
                : (language === "kn"
                  ? "ಮೋಹನ್ ಗೌಡ ಬಿಎಂ ಸಹ ಸಂಸ್ಥಾಪಕರು ಮತ್ತು ಕಾರ್ಯಾಚರಣೆಗಳನ್ನು ನೋಡಿಕೊಳ್ಳುತ್ತಾರೆ. ಸರಬರಾಜು ವ್ಯವಸ್ಥೆ, ಲಾಜಿಸ್ಟಿಕ್ಸ್ ಮತ್ತು ಗುಣಮಟ್ಟ ನಿರ್ವಹಣೆಯಲ್ಲಿ ಪ್ರಮುಖ ಪಾತ್ರವಹಿಸುತ್ತಾರೆ."
                  : "Mohan Gowda BM serves as the Co-Founder & Director, overseeing operations and farmer relations. He plays a key role in logistics coordination, supply chain management, and ensuring consistent product quality for farmers.")
              }
            </p>

            <div className="modalActions">

              <a
                href={
                  selectedOwner === "owner1"
                    ? "https://wa.me/919916178805"
                    : "tel:+919916178806"
                }
                className="modalBtn whatsappBtn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle size={18} />
                {language === "kn" ? "ವಾಟ್ಸಾಪ್" : "WhatsApp"}
              </a>

              <a
                href={
                  selectedOwner === "owner1"
                    ? "tel:+919916178805"
                    : "tel:+919916178806"
                }
                className="modalBtn callBtn"
              >
                <Phone size={18} />
                {language === "kn" ? "ಕಾಲ್ ಮಾಡಿ" : "Call Now"}
              </a>

            </div>

            <button onClick={() => setSelectedOwner(null)} className="closeBtn">
              {language === "kn" ? "ಮುಚ್ಚಿ" : "Close"}
            </button>
          </div>
        </div>
      )}
      <a href="https://wa.me/919916178805" className="whatsapp">
        <MessageCircle size={24} />
      </a>

      <footer className="footer">
        © {new Date().getFullYear()} Aadhar Dairy Farm & Silage
      </footer>

    </div>
  );
}